import {useAtom} from 'jotai';
import useProfile from 'modules/users/hooks/use-profile';
import persistentAtom from 'shared/atoms/persistent-atom';
import {AccessControlProps} from '../types';

export type PermissionProps = string | {name: string; exactPermission: boolean};

const cityIdAtom = persistentAtom<string | null>('cityId', null);
const companyIdAtom = persistentAtom<string | null>('companyId', null);
const isFranchiseAccessControlAtom = persistentAtom<boolean | null>(
  'isFranchiseAccessControl',
  null,
);

const useAccessControls = () => {
  const [cityId, setCityId] = useAtom<string | null>(cityIdAtom);
  const [companyId, setCompanyId] = useAtom<string | null>(companyIdAtom);
  const [isFranchiseAccessControl, setFranchiseAccessControl] = useAtom<
    boolean | null
  >(isFranchiseAccessControlAtom);

  const {data} = useProfile();

  const accessControl = data?.accessControls
    ?.filter((accessControl) => !accessControl?.isDeliveryManAccessControl)
    .find(
      (accessControl) =>
        (!!cityId && accessControl.cityId === cityId) ||
        (!!companyId && accessControl.companyId === companyId) ||
        (isFranchiseAccessControl && accessControl.isFranchiseAccessControl),
    );

  const userPermissions =
    accessControl?.permissionGroup?.permissions ?? ({} as any);

  const isAllowed = (permission: string, exactPermission?: boolean) => {
    const [module, modulePermission] = permission.split('.');

    if (!exactPermission && userPermissions[module]?.includes('all'))
      return true;

    return userPermissions[module]?.includes(modulePermission);
  };

  const isPermissionsAllowed = (permissions = [] as string[]) => {
    return permissions?.every((permission) => isAllowed(permission));
  };

  const isModuleAllowed = (permissions: PermissionProps[] = []) => {
    return permissions.some((permission) =>
      typeof permission === 'string'
        ? isAllowed(permission)
        : isAllowed(permission.name, permission.exactPermission),
    );
  };

  return {
    city: accessControl?.city,
    cityId,
    setCityId: setCityId as (cityId: string | null) => void,
    companyId,
    setCompanyId: setCompanyId as (companyId: string | null) => void,
    isPermissionsAllowed,
    isFranchiseAccessControl,
    setFranchiseAccessControl: setFranchiseAccessControl as (
      data: boolean | null,
    ) => void,
    isAllowed,
    isModuleAllowed,
    permissionGroup: accessControl?.permissionGroup,
    accessControl,
  };
};

export default useAccessControls;
